import { t } from "@lingui/macro";
import {
  type ClientFilterOptionsQuery,
  ClientOrderByColumnEnum,
  ClientsWhereColumn,
  SortOrder,
  SqlOperator,
} from "@src/__generated__/urql-graphql";
import type { IOption } from "@src/components/ui-kit";
import {
  commonQueryVariables,
  commonSearchParams,
} from "@src/utils/apolloHelpers";
import { Filter, Filters } from "@src/utils/components/filters/models";
import { OrderBy } from "@src/utils/components/sorting/OrderBy";
import { PaginationState } from "@src/utils/mobx/states/PaginationState";
import { action, computed, makeObservable, observable } from "mobx";
import type { ClientListItemModel } from "../models";

export class Store {
  pagination = new PaginationState("clients");
  refetch?: () => void = undefined;
  @observable clients: ClientListItemModel[] = [];
  @observable filtersSet = false;

  @observable searchTerm = "";

  orderBy = new OrderBy<ClientOrderByColumnEnum>([
    {
      column: ClientOrderByColumnEnum.ClientName,
      order: SortOrder.Asc,
    },
  ]);

  where = new Filters<ClientsWhereColumn>(
    [
      new Filter({
        column: ClientsWhereColumn.ResponsiblePersonUserId,
        operator: SqlOperator.In,
        title: t`Responsible manager`,
        options: [],
        value: [],
      }),
      new Filter({
        column: ClientsWhereColumn.ContactId,
        operator: SqlOperator.In,
        title: t`Contact person`,
        options: [],
        value: [],
      }),
      new Filter({
        column: ClientsWhereColumn.BrandId,
        operator: SqlOperator.In,
        title: t`Brand`,
        options: [],
        value: [],
      }),
    ],
    {
      tableKey: "clients",
      orderBy: this.orderBy,
    },
  );

  constructor() {
    makeObservable(this);
  }

  @computed get searchParams() {
    return commonSearchParams(this);
  }

  @computed get queryParams() {
    return commonQueryVariables(this);
  }

  @action.bound setFilterOptions(
    queryResponse: ClientFilterOptionsQuery | undefined,
  ) {
    if (!queryResponse) return;

    this.fillOptions(
      queryResponse.contacts,
      ClientsWhereColumn.ContactId,
      ({ first_name, last_name }) => `${first_name} ${last_name}`,
    );

    this.fillOptions(
      queryResponse.responsiblePersonsSimpleMap,
      ClientsWhereColumn.ResponsiblePersonUserId,
      ({ full_name }) => full_name,
    );

    this.fillOptions(
      queryResponse.brandsSimpleMap.reverse(),
      ClientsWhereColumn.BrandId,
      ({ name }) => name,
    );
  }

  @action.bound fillOptions<T extends { id: string }>(
    values: Array<T>,
    column: ClientsWhereColumn,
    labelGenerator: (value: T) => string,
  ): void {
    const valueCount = values.length;
    const filter = this.where.filtersByColumn.get(column);

    const options: IOption[] = [];

    for (let i = 0; i < valueCount; i++) {
      const value = values[i];

      options.push({
        value: value.id,
        label: labelGenerator(value),
      });
    }

    filter?.setOptions(options);
  }
}
